import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import SkillDemo from "../components/SkillDemo";
import { skills } from "../data/skills";
import { Star, ShoppingCart, Play, ArrowLeft, Users, Tag, Check, Sparkles } from "lucide-react";

import { useTranslation } from 'react-i18next';
function slugify(name) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function Stars({ rating }) {
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.5;
  return (
    <div className="flex items-center gap-0.5">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < full
              ? "fill-amber-500 text-amber-500"
              : i === full && hasHalf
              ? "fill-amber-500/50 text-amber-500"
              : "text-zinc-700"
          }`}
        />
      ))}
      <span className="ml-1 font-mono text-sm text-zinc-400">{rating}</span>
    </div>
  );
}

export default function SkillDetailPage() {
  const { t } = useTranslation();
  const { skillSlug } = useParams();
  const navigate = useNavigate();
  const [showDemo, setShowDemo] = useState(false);
  const [added, setAdded] = useState(false);

  const skill = useMemo(() => {
    return skills.find((s) => slugify(s.name) === skillSlug || String(s.id) === skillSlug);
  }, [skillSlug]);

  if (!skill) {
    return (
      <div className="min-h-screen bg-[#09090B] text-white">
        <Header />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">{t('skill_detail_page.skill_not_found')}</h1>
          <p className="text-zinc-400 mb-8">{t('skill_detail_page.the_skill_you_are_looking_for_was_not_found')}</p>
          <Link to="/skills" className="px-6 py-3 rounded-lg bg-amber-500 text-black font-medium hover:bg-amber-400 transition-colors">
            Back to Marketplace
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const skillSlugGenerated = slugify(skill.name);
  const isFree = skill.price === 0;

  const handleAdd = () => {
    if (isFree) {
      setAdded(true);
      setTimeout(() => setAdded(false), 3000);
    } else {
      navigate(`/checkout?skillId=${skill.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Header />
      <SEOHead
        title={`${skill.name} — Skill for Max OS Cloud | Max Employee`}
        description={skill.description}
        url={`/skills/${skillSlugGenerated}`}
        type="product"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: skill.name,
            description: skill.description,
            image: "https://maxemployee.com/og-image-homepage.webp",
            url: `https://maxemployee.com/skills/${skillSlugGenerated}`,
            offers: {
              "@type": "Offer",
              price: skill.price === 0 ? "0" : skill.price.toFixed(2),
              priceCurrency: skill.price === 0 ? "USD" : "BRL",
              availability: "https://schema.org/InStock",
            },
            ...(skill.rating ? {
              aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: String(skill.rating),
                ratingCount: String(skill.sales),
              },
            } : {}),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
              { "@type": "ListItem", position: 2, name: "Skills", item: "https://maxemployee.com/skills" },
              { "@type": "ListItem", position: 3, name: skill.name, item: `https://maxemployee.com/skills/${skillSlugGenerated}` },
            ],
          },
        ]}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-zinc-500 mb-8">
          <Link to="/skills" className="hover:text-amber-500 transition-colors">
            Marketplace
          </Link>
          <span>/</span>
          <Link to="/skills" className="hover:text-amber-500 transition-colors">
            Skills
          </Link>
          <span>/</span>
          <span className="text-zinc-300">{skill.name}</span>
        </nav>

        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">{t('skill_detail_page.back')}</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main content */}
          <div className="lg:col-span-2">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="text-5xl">{skill.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h1 className="text-3xl font-bold">{skill.name}</h1>
                  <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-500 border border-amber-500/20">
                    {skill.category}
                  </span>
                  {isFree && (
                    <span className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{t('skill_detail_page.free')}</span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-zinc-400">
                  <span>by <span className="text-amber-500">{skill.author}</span></span>
                  <Stars rating={skill.rating} />
                  <span className="flex items-center gap-1">
                    <Users className="w-3.5 h-3.5" />
                    {skill.sales} sales
                  </span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3 text-zinc-200">{t('skill_detail_page.description')}</h2>
              <p className="text-zinc-400 leading-relaxed">{skill.description}</p>
            </div>

            {/* Tags */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3 text-zinc-200">{t('skill_detail_page.tags')}</h2>
              <div className="flex flex-wrap gap-2">
                {skill.tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-xs text-zinc-400"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Steps */}
            {skill.steps && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold mb-3 text-zinc-200">{t('skill_detail_page.how_it_works')}</h2>
                <div className="space-y-3">
                  {skill.steps.split("\n").filter(Boolean).map((step, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5">
                        <span className="text-xs font-mono text-amber-500">{i + 1}</span>
                      </div>
                      <p className="text-zinc-400 text-sm leading-relaxed">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Demo section */}
            <div className="mb-8">
              <button
                onClick={() => setShowDemo(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-500 transition-colors"
              >
                <Play className="w-4 h-4" />
                <span className="text-sm font-medium">{t('skill_detail_page.view_demo')}</span>
              </button>
            </div>
          </div>

          {/* Sidebar - Purchase card */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 bg-zinc-950 border border-zinc-900 p-6 rounded-lg">
              <div className="text-center mb-6">
                <div className="text-4xl font-bold text-white mb-1">
                  {isFree ? "Free" : `$${skill.price.toFixed(2)}`}
                </div>
                {!isFree && (
                  <div className="text-xs text-zinc-500 font-mono">{t('skills.one_time_payment')}</div>
                )}
              </div>

              <button
                onClick={handleAdd}
                className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors mb-3 ${
                  added
                    ? "bg-emerald-600 text-white"
                    : "bg-amber-500 text-black hover:bg-amber-400"
                }`}
              >
                {added ? (
                  <>
                    <Check className="w-4 h-4" />
                    Added!
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" />
                    {isFree ? "Add to Agent" : "Buy"}
                  </>
                )}
              </button>

              <div className="space-y-3 mt-6 pt-6 border-t border-zinc-900">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('skill_detail_page.category')}</span>
                  <span className="text-zinc-300">{skill.category}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('skill_detail_page.rating')}</span>
                  <span className="text-zinc-300 flex items-center gap-1">
                    <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                    {skill.rating}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('skill_detail_page.sales')}</span>
                  <span className="text-zinc-300">{skill.sales}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">{t('skill_detail_page.author')}</span>
                  <span className="text-amber-500">{skill.author}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-zinc-900">
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{t('skill_detail_page.includes_future_updates')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Demo modal */}
      {showDemo && (
        <SkillDemo skill={skill} onClose={() => setShowDemo(false)} />
      )}
    </div>
  );
}
